import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";

export type AccountTier = "universe" | "supporter" | "team";

type AccountCtx = {
	tier: AccountTier;
	refresh: () => Promise<void>;
};

const AccountContext = createContext<AccountCtx | null>(null);

export function AccountProvider({ children }: { children: ReactNode }) {
	const [tier, setTier] = useState<AccountTier>("universe");

	const refresh = useCallback(async () => {
		try {
			const r = await fetch("/api/account", { credentials: "include" });
			const d = (await r.json()) as { tier?: string };
			if (d.tier === "supporter" || d.tier === "team") setTier(d.tier);
			else setTier("universe");
		} catch {
			setTier("universe");
		}
	}, []);

	useEffect(() => {
		void refresh();
	}, [refresh]);

	useEffect(() => {
		const fn = () => {
			void refresh();
		};
		window.addEventListener("wi-account-changed", fn);
		return () => window.removeEventListener("wi-account-changed", fn);
	}, [refresh]);

	const value = useMemo(() => ({ tier, refresh }), [tier, refresh]);

	return <AccountContext.Provider value={value}>{children}</AccountContext.Provider>;
}

export function useAccount(): AccountCtx {
	const v = useContext(AccountContext);
	if (!v) throw new Error("useAccount must be used within AccountProvider");
	return v;
}
